import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface ExploreCategoryCard {
  id: string;
  title: string;
  description: string;
}

interface QuickGuideSection {
  id: string;
  title: string;
  description: string;
}

interface Itinerary {
  duration: string;
  title: string;
  description: string;
}

interface PracticalTips {
  transport: string;
  accommodation: string;
  safety: string;
  emergency: string;
}

interface NearbyOverviewContent {
  pageTitle: string;
  pageSubtitle: string;
  mainDescription: string;
  exploreCategoryTitle: string;
  categories: ExploreCategoryCard[];
  quickGuideTitle: string;
  quickGuideIntro: string;
  guides: QuickGuideSection[];
  itinerariesTitle: string;
  itineraries: Itinerary[];
  practicalTipsTitle: string;
  practiceTips: PracticalTips;
}

export default function NearbySectionOverviewManager() {
  const [content, setContent] = useState<NearbyOverviewContent>({
    pageTitle: 'Explore Nearby',
    pageSubtitle: 'Discover sacred temples, scenic spots & cultural experiences',
    mainDescription: 'Muchukunnu and its surroundings offer a varied experience for devotees and visitors: sacred temples, serene backwaters, scenic viewpoints, heritage trails, and coastal beaches. Use the sections below to quickly jump to curated lists, practical travel information, and suggested itineraries to make the most of your visit.',
    exploreCategoryTitle: 'Explore By Category',
    categories: [
      {
        id: 'beaches',
        title: 'Beaches & Coast',
        description: 'Family-friendly beaches and safe bathing spots with nearby facilities and timings.'
      },
      {
        id: 'boating',
        title: 'Boating & Backwaters',
        description: 'Guided boat services, timings, and contact details for scenic backwater experiences.'
      },
      {
        id: 'temples',
        title: 'Temples Nearby',
        description: 'Practical directory of nearby shrines with distance estimates and timings.'
      },
      {
        id: 'viewpoints',
        title: 'Viewpoints & Heritage',
        description: 'Short treks and heritage walking loops with panoramic views.'
      },
      {
        id: 'activities',
        title: 'Activities & Services',
        description: 'Guided heritage walks, transport providers, homestays, and local guides.'
      }
    ],
    quickGuideTitle: 'Quick Guide to Nearby Sections',
    quickGuideIntro: 'Below are short summaries of each Explore Nearby page — useful to plan your devotional day and side trips.',
    guides: [
      {
        id: 'beaches',
        title: 'Beaches',
        description: 'Details on the safest bathing spots, lifeguard hours, parking, and nearby refreshments; recommended for evening visits and sunset viewing.'
      },
      {
        id: 'boating',
        title: 'Boating',
        description: 'Backwater and river cruises, local boat operator contacts, and sample routes — perfect for reflective, scenic experiences after darshan.'
      },
      {
        id: 'viewpoints',
        title: 'Viewpoints & Heritage Trails',
        description: 'Short treks and heritage walking loops that explain local history and offer panoramic views; suitable for morning outings.'
      },
      {
        id: 'temples',
        title: 'Temples Nearby',
        description: 'A practical directory of nearby shrines, with distance estimates from Muchukunnu Temple and timings for daily rituals and festivals.'
      },
      {
        id: 'activities',
        title: 'Activities & Services',
        description: 'Guided heritage walks, transport providers, homestays, and local guides to help you extend your pilgrimage into a cultural visit.'
      }
    ],
    itinerariesTitle: 'Suggested Itineraries',
    itineraries: [
      {
        duration: 'HALF DAY',
        title: 'Half-day Pilgrim',
        description: 'Morning darshan at Muchukunnu Temple → Short heritage walk → Lunch at local annadanam center. Best for quick visits.'
      },
      {
        duration: 'FULL DAY',
        title: 'Full-day Cultural',
        description: 'Darshan and ritual in the morning → Boat ride through backwaters → Visit nearby temples and viewpoint for sunset → Dinner.'
      }
    ],
    practicalTipsTitle: 'Practical Tips & Contacts',
    practiceTips: {
      transport: 'Local taxi and auto-rickshaw services operate from the temple; pre-book vehicles for festival days.',
      accommodation: 'Several homestays and lodging options exist nearby — check the \'Activities\' and \'Tourist Places\' pages for recommendations.',
      safety: 'Follow local guidance on weather/sea conditions for beaches and boating. Carry water and sun protection.',
      emergency: 'Temple office: +91-XXXXXXXXXX. For medical emergencies dial local emergency services.'
    }
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    const unsubscribe = loadContent();
    return () => unsubscribe();
  }, []);

  const loadContent = () => {
    try {
      console.log('🔄 NearbySectionOverviewManager: Setting up real-time listener...');
      const docRef = doc(db, 'nearby_overview_content', 'main');
      setLoading(true);

      const unsubscribe = onSnapshot(docRef, (docSnap) => {
        try {
          console.group('📊 NearbySectionOverviewManager Listener Event');
          console.log('Document exists:', docSnap.exists());
          console.log('Document ID:', docSnap.id);
          console.log('Raw data:', docSnap.data());
          console.groupEnd();

          if (docSnap.exists()) {
            const data = docSnap.data() as any;
            // Merge with defaults to ensure all properties exist
            const mergedData: NearbyOverviewContent = {
              pageTitle: data.pageTitle || 'Explore Nearby',
              pageSubtitle: data.pageSubtitle || 'Discover sacred temples, scenic spots & cultural experiences',
              mainDescription: data.mainDescription || '',
              exploreCategoryTitle: data.exploreCategoryTitle || 'Explore By Category',
              categories: Array.isArray(data.categories) ? data.categories : [],
              quickGuideTitle: data.quickGuideTitle || 'Quick Guide to Nearby Sections',
              quickGuideIntro: data.quickGuideIntro || '',
              guides: Array.isArray(data.guides) ? data.guides : [],
              itinerariesTitle: data.itinerariesTitle || 'Suggested Itineraries',
              itineraries: Array.isArray(data.itineraries) ? data.itineraries : [],
              practicalTipsTitle: data.practicalTipsTitle || 'Practical Tips & Contacts',
              practiceTips: data.practiceTips || { transport: '', accommodation: '', safety: '', emergency: '' }
            };
            setContent(mergedData);
            console.log('✅ Admin: Real-time content loaded from Firebase');

          } else {
            console.warn('⚠️ Admin: Document does not exist, using defaults');
          }
        } catch (error) {
          console.error('Error processing content:', error);
        } finally {
          setLoading(false);
        }
      }, (error) => {
        console.error('❌ Admin Error in onSnapshot:', error);
        console.error('Error code:', (error as any).code);
        setSnackbar({ open: true, message: 'Error loading content', severity: 'error' });
        setLoading(false);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error setting up listener:', error);
      setSnackbar({ open: true, message: 'Error loading content', severity: 'error' });
      setLoading(false);
      return () => {};
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      console.log('💾 Saving nearby overview content to Firebase...');
      console.log('Full content object:', JSON.stringify(content, null, 2));

      const docRef = doc(db, 'nearby_overview_content', 'main');
      await setDoc(docRef, content);

      console.log('✅ Content saved successfully to nearby_overview_content/main');
      console.log('Document reference:', docRef.path);
      setSnackbar({ open: true, message: 'Content saved successfully!', severity: 'success' });
    } catch (error) {
      console.error('❌ Error saving content:', error);
      setSnackbar({ open: true, message: 'Error saving content', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof NearbyOverviewContent, value: string | any) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  const handleCategoryChange = (index: number, field: keyof ExploreCategoryCard, value: string) => {
    const newCategories = [...content.categories];
    newCategories[index] = { ...newCategories[index], [field]: value };
    setContent(prev => ({ ...prev, categories: newCategories }));
  };

  const handleGuideChange = (index: number, field: keyof QuickGuideSection, value: string) => {
    const newGuides = [...content.guides];
    newGuides[index] = { ...newGuides[index], [field]: value };
    setContent(prev => ({ ...prev, guides: newGuides }));
  };

  const handleItineraryChange = (index: number, field: keyof Itinerary, value: string) => {
    const newItineraries = [...content.itineraries];
    newItineraries[index] = { ...newItineraries[index], [field]: value };
    setContent(prev => ({ ...prev, itineraries: newItineraries }));
  };

  const handleTipsChange = (field: keyof PracticalTips, value: string) => {
    setContent(prev => ({ ...prev, practiceTips: { ...prev.practiceTips, [field]: value } }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
            Nearby Section Overview
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Manage the main explore nearby page content
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadContent}
            disabled={loading}
            variant="outlined"
            sx={{
              borderColor: GOLD,
              color: GOLD,
              '&:hover': { bgcolor: `${GOLD}15` }
            }}
          >
            Refresh
          </Button>
          <Button
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      {/* Page Header */}
      <Paper elevation={0} sx={{
        p: 3,
        mb: 3,
        border: `2px solid ${GOLD}`,
        borderRadius: 2,
        bgcolor: '#fafaf8'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          📄 Page Header
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Page Title"
              value={content.pageTitle}
              onChange={(e) => handleChange('pageTitle', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: GOLD },
                  '&.Mui-focused fieldset': { borderColor: GOLD }
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Page Subtitle"
              value={content.pageSubtitle}
              onChange={(e) => handleChange('pageSubtitle', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: GOLD },
                  '&.Mui-focused fieldset': { borderColor: GOLD }
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Main Description"
              value={content.mainDescription}
              onChange={(e) => handleChange('mainDescription', e.target.value)}
              multiline
              rows={4}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: GOLD },
                  '&.Mui-focused fieldset': { borderColor: GOLD }
                }
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Explore by Category Cards */}
      <Paper elevation={0} sx={{
        p: 3,
        mb: 3,
        border: `2px solid ${GOLD}`,
        borderRadius: 2,
        bgcolor: '#fafaf8'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          🗺️ Explore By Category
        </Typography>
        <TextField
          fullWidth
          label="Section Title"
          value={content.exploreCategoryTitle}
          onChange={(e) => handleChange('exploreCategoryTitle', e.target.value)}
          size="small"
          sx={{ mb: 3, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
        />
        <Grid container spacing={2}>
          {(content.categories || []).map((category, index) => (
            <Grid item xs={12} sm={6} key={category.id}>
              <Paper elevation={0} sx={{ p: 2, border: `1px solid ${GOLD}40`, bgcolor: '#fff' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: GOLD, mb: 1, display: 'block' }}>
                  Category {index + 1}
                </Typography>
                <TextField
                  fullWidth
                  label="Title"
                  value={category.title}
                  onChange={(e) => handleCategoryChange(index, 'title', e.target.value)}
                  size="small"
                  sx={{ mb: 1, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                />
                <TextField
                  fullWidth
                  label="Description"
                  value={category.description}
                  onChange={(e) => handleCategoryChange(index, 'description', e.target.value)}
                  multiline
                  rows={2}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                />
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Quick Guide Section */}
      <Paper elevation={0} sx={{
        p: 3,
        mb: 3,
        border: `2px solid ${GOLD}`,
        borderRadius: 2,
        bgcolor: '#fafaf8'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          📚 Quick Guide to Nearby Sections
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Section Title"
              value={content.quickGuideTitle}
              onChange={(e) => handleChange('quickGuideTitle', e.target.value)}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Intro Text"
              value={content.quickGuideIntro}
              onChange={(e) => handleChange('quickGuideIntro', e.target.value)}
              multiline
              rows={2}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          {(content.guides || []).map((guide, index) => (
            <Grid item xs={12} sm={6} key={guide.id}>
              <Paper elevation={0} sx={{ p: 2, border: `1px solid ${GOLD}40`, bgcolor: '#fff' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: GOLD, mb: 1, display: 'block' }}>
                  Guide {index + 1}
                </Typography>
                <TextField
                  fullWidth
                  label="Title"
                  value={guide.title}
                  onChange={(e) => handleGuideChange(index, 'title', e.target.value)}
                  size="small"
                  sx={{ mb: 1, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                />
                <TextField
                  fullWidth
                  label="Description"
                  value={guide.description}
                  onChange={(e) => handleGuideChange(index, 'description', e.target.value)}
                  multiline
                  rows={3}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                />
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Suggested Itineraries */}
      <Paper elevation={0} sx={{
        p: 3,
        mb: 3,
        border: `2px solid ${GOLD}`,
        borderRadius: 2,
        bgcolor: '#fafaf8'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          ⏱️ Suggested Itineraries
        </Typography>
        <TextField
          fullWidth
          label="Section Title"
          value={content.itinerariesTitle}
          onChange={(e) => handleChange('itinerariesTitle', e.target.value)}
          size="small"
          sx={{ mb: 3, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
        />
        <Grid container spacing={2}>
          {(content.itineraries || []).map((itinerary, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Paper elevation={0} sx={{ p: 2, border: `1px solid ${GOLD}40`, bgcolor: '#fff' }}>
                <TextField
                  fullWidth
                  label="Duration"
                  value={itinerary.duration}
                  onChange={(e) => handleItineraryChange(index, 'duration', e.target.value)}
                  size="small"
                  sx={{ mb: 1, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                />
                <TextField
                  fullWidth
                  label="Itinerary Title"
                  value={itinerary.title}
                  onChange={(e) => handleItineraryChange(index, 'title', e.target.value)}
                  size="small"
                  sx={{ mb: 1, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                />
                <TextField
                  fullWidth
                  label="Description"
                  value={itinerary.description}
                  onChange={(e) => handleItineraryChange(index, 'description', e.target.value)}
                  multiline
                  rows={3}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                />
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Practical Tips & Contacts */}
      <Paper elevation={0} sx={{
        p: 3,
        border: `2px solid ${GOLD}`,
        borderRadius: 2,
        bgcolor: '#fafaf8'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          ℹ️ Practical Tips & Contacts
        </Typography>
        <TextField
          fullWidth
          label="Section Title"
          value={content.practicalTipsTitle}
          onChange={(e) => handleChange('practicalTipsTitle', e.target.value)}
          size="small"
          sx={{ mb: 3, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
        />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="🚕 Transport"
              value={content.practiceTips.transport}
              onChange={(e) => handleTipsChange('transport', e.target.value)}
              multiline
              rows={2}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="🏠 Accommodation"
              value={content.practiceTips.accommodation}
              onChange={(e) => handleTipsChange('accommodation', e.target.value)}
              multiline
              rows={2}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="⚠️ Safety"
              value={content.practiceTips.safety}
              onChange={(e) => handleTipsChange('safety', e.target.value)}
              multiline
              rows={2}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="🆘 Emergency"
              value={content.practiceTips.emergency}
              onChange={(e) => handleTipsChange('emergency', e.target.value)}
              multiline
              rows={2}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity as 'success' | 'error'}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
